import type ApiError from "utils/errors/apiError";

import { useOpenGlobalErrorModal } from "hooks/useGlobalError";
import { useCallback, useEffect, useRef, useState } from "react";
import { devLog } from "utils/devtools/devLogging";
import { shallowEqual } from "utils/shallowEqual";

interface IFetchingStateOptions {
  allowRetry: boolean;
  ignoreLoadingState: boolean; // DISCLAIMER (Daniel): This will make it possible for multiple concurrent fetches. Use with caution (you may not know the order of the results responses)
  onError?: (error: ApiError) => void;
  suppressError?: boolean;
}

type TFetchingState = "Error" | "Initial" | "Loaded" | "Loading" | "ShouldRetry";

export const useFetchedState = <TFetchParams extends unknown[], TFetchResult>(
  fetchingMethod: (...args: TFetchParams) => Promise<TFetchResult>,
  options: Partial<IFetchingStateOptions> = {
    allowRetry: true,
    suppressError: false,
  },
) => {
  const { onError, suppressError } = options;
  const allowRetry = options.allowRetry ?? true;

  const [data, setData] = useState<null | TFetchResult>(null);
  const fetchingStateRef = useRef<TFetchingState>("Initial");
  const [fetchingState, setFetchingState] = useState<TFetchingState>("Initial");
  const openGlobalErrorModal = useOpenGlobalErrorModal();

  const [previousArgs, setPreviousArgs] = useState<TFetchParams>();

  const loadData = useCallback(
    async (...args: TFetchParams) => {
      setPreviousArgs((prevArgs) => (shallowEqual(prevArgs, args) ? prevArgs : args)); // since args is destructured, its reference changes on every call even if empty
      if (!(fetchingStateRef.current === "Initial" || fetchingStateRef.current === "ShouldRetry")) return;
      try {
        fetchingStateRef.current = "Loading";
        setFetchingState(fetchingStateRef.current);
        const newData = await fetchingMethod(...args);
        setData(newData);
        fetchingStateRef.current = "Loaded";
        setFetchingState(fetchingStateRef.current);
      } catch (error) {
        fetchingStateRef.current = "Error";
        const retryLoadData = async () => {
          fetchingStateRef.current = "ShouldRetry";
          setFetchingState(fetchingStateRef.current);
          // eslint-disable-next-line react-hooks/immutability -- Legacy
          await loadData(...args);
        };

        if (!suppressError) {
          openGlobalErrorModal(error as Error, allowRetry ? retryLoadData : undefined);
        } else {
          setFetchingState(fetchingStateRef.current);
          devLog({ extra: error as Error, level: "error", message: "useFetchedState error:" });
        }

        onError?.(error as ApiError);
      }
    },
    [allowRetry, fetchingMethod, onError, openGlobalErrorModal, suppressError],
  );

  const forceLoadData = useCallback(
    async (...args: TFetchParams) => {
      setPreviousArgs(args);
      if (fetchingStateRef.current === "Loading" && !options.ignoreLoadingState) return;
      try {
        fetchingStateRef.current = "Loading";
        setFetchingState(fetchingStateRef.current);
        const newData = await fetchingMethod(...args);
        setData(newData);
        fetchingStateRef.current = "Loaded";
        setFetchingState(fetchingStateRef.current);
      } catch (error) {
        fetchingStateRef.current = "Error";
        setFetchingState(fetchingStateRef.current);
        // eslint-disable-next-line react-hooks/immutability -- Legacy
        const retryLoadData = async () => forceLoadData(...args);
        openGlobalErrorModal(error as Error, allowRetry ? retryLoadData : undefined);
      }
    },
    [allowRetry, fetchingMethod, openGlobalErrorModal, options.ignoreLoadingState],
  );

  const retryAction = useCallback(async () => {
    fetchingStateRef.current = "ShouldRetry";
    setFetchingState(fetchingStateRef.current);

    await loadData(...((previousArgs || []) as TFetchParams));
  }, [loadData, previousArgs]);

  useEffect(() => {
    fetchingStateRef.current = "Initial";
    setFetchingState(fetchingStateRef.current);
  }, [fetchingMethod]);

  const isLoading = fetchingStateRef.current === "Loading";

  return { data, fetchingState, forceLoadData, isLoading, loadData, retryAction, setData };
};
